import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../../utils/fetchAPI';
import { useCityReference } from '../city/useCityReference';
import { translateApiMessage } from '../../utils/apiMessages';

const DEFAULT_PAGE_SIZE = 20;

/**
 * The neighbourhood feed and its filters.
 *
 * As with the directory, the filters live in the URL: a resident can send
 * "everything posted about the station district" as a link.
 */
export function useFeed() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { city, neighborhoods, cityParam } = useCityReference();

  const filters = useMemo(
    () => ({
      type: searchParams.get('type') || '',
      neighborhood: searchParams.get('neighborhood') || '',
      q: searchParams.get('q') || '',
      page: Number(searchParams.get('page')) || 1,
    }),
    [searchParams]
  );

  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Bumped after publishing, so the feed reloads without touching the filters.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const params = new URLSearchParams();
      // A visitor says which city they are looking at; a resident's account
      // already says it.
      if (cityParam) params.set('city', cityParam);
      if (filters.type) params.set('type', filters.type);
      if (filters.neighborhood) params.set('neighborhood', filters.neighborhood);
      if (filters.q) params.set('q', filters.q);
      params.set('page', String(filters.page));
      params.set('limit', String(DEFAULT_PAGE_SIZE));

      try {
        const response = await apiCall(`/posts?${params.toString()}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(translateApiMessage(data.error) || 'Le fil du quartier n’a pas pu être chargé.');
        }

        setPosts(data.data || []);
        setMeta(data.meta || null);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setPosts([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken, cityParam]);

  const setFilter = useCallback(
    (name, value) => {
      const next = new URLSearchParams(searchParams);

      if (value) {
        next.set(name, value);
      } else {
        next.delete(name);
      }

      // Narrowing while on page 4 would show an empty list.
      if ('page' !== name) {
        next.delete('page');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return {
    filters,
    posts,
    meta,
    city,
    neighborhoods,
    loading,
    error,
    setFilter,
    resetFilters,
    reload,
    pageSize: DEFAULT_PAGE_SIZE,
  };
}
